import React, { useEffect, useMemo, useState } from "react";
import { Topbar } from "@/components/layout/Topbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { AlertTriangle, Send, FlaskConical } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useSelectedDate } from "@/context/DateContext";
import { RaceSelect } from "@/components/rikkus/RaceSelect";
import { StatusBadge } from "@/components/rikkus/StatusBadge";
import { TimestampET } from "@/components/rikkus/TimestampET";

const BET_TYPES = ["WIN", "TOP_K", "H2H", "MULTI_HORSE", "OTHER"];
const SIDES = ["FOR", "AGAINST"];
const NOT_TAKEN_REASONS = ["INSUFFICIENT_EDGE", "NO_COUNTERPARTY", "WITHDRAWN", "MISSED_WINDOW", "LIQUIDITY_LIMIT", "OTHER"];

export default function OffersPage() {
    const { date } = useSelectedDate();
    const nav = useNavigate();
    const [tab, setTab] = useState("taken");
    const [raceId, setRaceId] = useState(null);
    const [pseudo, setPseudo] = useState("");
    const [betType, setBetType] = useState("WIN");
    const [kThresh, setKThresh] = useState("");
    const [horses, setHorses] = useState("");
    const [side, setSide] = useState("FOR");
    const [stakeA, setStakeA] = useState("");
    const [stakeB, setStakeB] = useState("");
    const [terms, setTerms] = useState("");
    const [counterparty, setCounterparty] = useState("");
    const [reason, setReason] = useState("INSUFFICIENT_EDGE");
    const [offers, setOffers] = useState([]);
    const [saving, setSaving] = useState(false);

    const implied = useMemo(() => {
        const a = parseFloat(stakeA);
        const b = parseFloat(stakeB);
        if (!isFinite(a) || !isFinite(b) || a + b <= 0) return null;
        if (a === b) return 0.5;
        return a / (a + b);
    }, [stakeA, stakeB]);

    const load = () => {
        api.get(`/circle-offers?date=${date}`).then((r) => setOffers(r.data || [])).catch(() => setOffers([]));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

    const resetForm = () => {
        setBetType("WIN"); setKThresh(""); setHorses(""); setSide("FOR");
        setStakeA(""); setStakeB(""); setTerms(""); setCounterparty(""); setReason("INSUFFICIENT_EDGE");
    };

    const submit = async () => {
        if (!raceId) { toast.error("Selecciona una carrera"); return; }
        if (!pseudo.trim()) { toast.error("Pseudónimo del oferente requerido"); return; }
        const horseList = horses.split(",").map(s => s.trim()).filter(Boolean);
        if (horseList.length === 0) { toast.error("Ingresa al menos un caballo (ej. 3 o 3,5)"); return; }
        if (!terms.trim()) { toast.error("Pega el texto original de la oferta"); return; }
        if (betType === "TOP_K" && (!kThresh || parseInt(kThresh, 10) < 1)) {
            toast.error("TOP_K requiere k_threshold >= 1"); return;
        }
        const isTaken = tab === "taken";
        const body = {
            race_id: raceId,
            offering_pseudonym: pseudo.trim(),
            bet_type: betType,
            k_threshold: betType === "TOP_K" ? parseInt(kThresh, 10) : null,
            horse_program_numbers: horseList,
            side,
            stake_side_a: stakeA ? parseFloat(stakeA) : null,
            stake_side_b: stakeB ? parseFloat(stakeB) : null,
            terms_raw_text: terms,
            was_taken: isTaken,
            not_taken_reason: !isTaken ? reason : null,
            counterparty_pseudonym: isTaken && counterparty.trim() ? counterparty.trim() : null,
        };
        setSaving(true);
        try {
            const { data } = await api.post("/circle-offers", body);
            toast.success(`Oferta registrada #${data.offer_id}`);
            resetForm();
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || err.message || "Error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Topbar title="Ofertas del círculo" subtitle="Captura rápida: tomadas y NO tomadas (clave de selección adversa)" />
            <div className="p-3 md:p-4 grid grid-cols-1 lg:grid-cols-5 gap-4 flex-1 overflow-auto">
                <div className="lg:col-span-2">
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm">Registrar oferta</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Tabs value={tab} onValueChange={setTab}>
                                <TabsList className="grid grid-cols-2 mb-3 h-9">
                                    <TabsTrigger data-testid="offers-tab-taken" value="taken">Tomada</TabsTrigger>
                                    <TabsTrigger data-testid="offers-tab-not-taken" value="not_taken">No tomada</TabsTrigger>
                                </TabsList>
                                <form data-testid="offers-capture-form" onSubmit={(e) => { e.preventDefault(); submit(); }} className="space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="col-span-2">
                                            <Label className="text-xs mb-1 block">Carrera</Label>
                                            <RaceSelect value={raceId} onChange={setRaceId} testid="offers-race-select" />
                                        </div>
                                        <div className="col-span-2">
                                            <Label className="text-xs mb-1 block">Pseudónimo oferente</Label>
                                            <Input data-testid="offers-pseudonym-input" value={pseudo} onChange={(e) => setPseudo(e.target.value)} className="h-9" placeholder="CoyoteCharlie" />
                                        </div>
                                        <div>
                                            <Label className="text-xs mb-1 block">Tipo</Label>
                                            <Select value={betType} onValueChange={setBetType}>
                                                <SelectTrigger data-testid="offers-bet-type-select" className="h-9"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    {BET_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label className="text-xs mb-1 block">Side</Label>
                                            <Select value={side} onValueChange={setSide}>
                                                <SelectTrigger data-testid="offers-side-select" className="h-9"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    {SIDES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        {betType === "TOP_K" && (
                                            <div>
                                                <Label className="text-xs mb-1 block">k</Label>
                                                <Input data-testid="offers-k-input" type="number" min={1} value={kThresh} onChange={(e) => setKThresh(e.target.value)} className="h-9 font-mono" />
                                            </div>
                                        )}
                                        <div className={betType === "TOP_K" ? "" : "col-span-2"}>
                                            <Label className="text-xs mb-1 block">Caballos (# programa)</Label>
                                            <Input data-testid="offers-horses-input" value={horses} onChange={(e) => setHorses(e.target.value)} className="h-9 font-mono" placeholder="7 o 3,5" />
                                        </div>
                                        <div>
                                            <Label className="text-xs mb-1 block">Stake A</Label>
                                            <Input data-testid="offers-stake-a-input" type="number" step="0.01" value={stakeA} onChange={(e) => setStakeA(e.target.value)} className="h-9 font-mono" />
                                        </div>
                                        <div>
                                            <Label className="text-xs mb-1 block">Stake B</Label>
                                            <Input data-testid="offers-stake-b-input" type="number" step="0.01" value={stakeB} onChange={(e) => setStakeB(e.target.value)} className="h-9 font-mono" />
                                        </div>
                                    </div>
                                    <div className="text-xs text-muted-foreground font-mono" data-testid="offers-implied-prob">
                                        implied_probability = {implied != null ? implied.toFixed(4) : "—"}
                                    </div>
                                    <div>
                                        <Label className="text-xs mb-1 block">Texto original (OBLIGATORIO)</Label>
                                        <Textarea data-testid="offers-terms-input" value={terms} onChange={(e) => setTerms(e.target.value)} rows={3} placeholder="Pega el mensaje íntegro del grupo" className="font-mono text-xs" />
                                    </div>

                                    <TabsContent value="taken" className="m-0">
                                        <div>
                                            <Label className="text-xs mb-1 block">Contraparte (pseudónimo)</Label>
                                            <Input data-testid="offers-counterparty-input" value={counterparty} onChange={(e) => setCounterparty(e.target.value)} className="h-9" />
                                        </div>
                                    </TabsContent>
                                    <TabsContent value="not_taken" className="m-0">
                                        <div className="rounded-md border border-warn/40 bg-warn-bg p-3 space-y-2">
                                            <div className="flex items-center gap-2 text-warn">
                                                <AlertTriangle className="h-3.5 w-3.5" />
                                                <span className="text-xs font-semibold uppercase tracking-wide">Selección adversa (clave)</span>
                                            </div>
                                            <div>
                                                <Label className="text-xs mb-1 block">Motivo</Label>
                                                <Select value={reason} onValueChange={setReason}>
                                                    <SelectTrigger data-testid="offers-not-taken-reason" className="h-9"><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        {NOT_TAKEN_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                    </TabsContent>

                                    <div className="pt-2">
                                        <Button data-testid="offers-submit-button" type="submit" disabled={saving} className="w-full h-9">
                                            <Send className="h-3.5 w-3.5 mr-2" />
                                            {saving ? "Guardando…" : "Guardar oferta"}
                                        </Button>
                                    </div>
                                </form>
                            </Tabs>
                        </CardContent>
                    </Card>
                </div>
                <div className="lg:col-span-3">
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm">Ofertas del día — {date}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="rounded-md border border-border overflow-hidden">
                                <Table data-testid="offers-today-table">
                                    <TableHeader>
                                        <TableRow className="bg-muted/40 hover:bg-muted/40">
                                            <TableHead className="h-8 text-[11px]">#</TableHead>
                                            <TableHead className="h-8 text-[11px]">Ofertante</TableHead>
                                            <TableHead className="h-8 text-[11px]">Tipo</TableHead>
                                            <TableHead className="h-8 text-[11px]">Caballos</TableHead>
                                            <TableHead className="h-8 text-[11px]">Side</TableHead>
                                            <TableHead className="h-8 text-[11px] text-right">Impl.</TableHead>
                                            <TableHead className="h-8 text-[11px]">Estado</TableHead>
                                            <TableHead className="h-8 text-[11px]">Ofrecida</TableHead>
                                            <TableHead className="h-8 text-[11px]"></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {offers.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={9} className="text-center text-xs text-muted-foreground py-8">
                                                    Sin ofertas para {date}.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {offers.map((o) => (
                                            <TableRow key={o.offer_id} data-testid={`offers-row-${o.offer_id}`} className="h-9">
                                                <TableCell className="font-mono text-xs">{o.offer_id}</TableCell>
                                                <TableCell className="text-xs">{o.offering_pseudonym}</TableCell>
                                                <TableCell className="font-mono text-xs">
                                                    {o.bet_type}{o.k_threshold ? `(${o.k_threshold})` : ""}
                                                </TableCell>
                                                <TableCell className="font-mono text-xs">{(o.horse_program_numbers || []).join(",")}</TableCell>
                                                <TableCell className="font-mono text-xs">{o.side}</TableCell>
                                                <TableCell className="text-right font-mono text-xs">
                                                    {o.implied_probability != null ? Number(o.implied_probability).toFixed(4) : "—"}
                                                </TableCell>
                                                <TableCell>
                                                    {o.was_taken === true && <StatusBadge kind="verified">TOMADA</StatusBadge>}
                                                    {o.was_taken === false && <StatusBadge kind="partial">NO TOMADA</StatusBadge>}
                                                    {o.was_taken == null && <StatusBadge kind="pending">PENDIENTE</StatusBadge>}
                                                </TableCell>
                                                <TableCell><TimestampET value={o.offered_at} /></TableCell>
                                                <TableCell className="text-right">
                                                    <Button
                                                        data-testid={`offers-simulate-btn-${o.offer_id}`}
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-7 px-2 text-xs"
                                                        onClick={() => nav(`/simulate?fromOffer=${o.offer_id}`)}
                                                    >
                                                        <FlaskConical className="h-3 w-3 mr-1" /> Simular
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </>
    );
}
